import { pushDataLayerEvent, type DataLayerWindow } from './core';
import type { SearchDataLayerEvent } from './types';

function getSearchPageNumber(location: Location): number {
  const pageValue = new URL(location.href).searchParams.get('page');
  const pageNumber = Number(pageValue ?? '1');
  return Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1;
}

export function extractSearchEvent(
  root: HTMLElement,
  location: Location
): SearchDataLayerEvent | null {
  if (getSearchPageNumber(location) > 1) return null;

  const explicitState = root.dataset.searchState?.trim().toLocaleLowerCase();
  const hasRenderedError = Boolean(root.querySelector('.search-results__error-region'));
  const hasRenderedNoResults = Boolean(root.querySelector('.search-results__no-results-head'));
  const renderedSearchQuery = root
    .querySelector<HTMLElement>('.search-results__heading-query')
    ?.textContent?.trim();
  const fallbackResultCount = root
    .querySelector<HTMLElement>('.search-results__see-more-wrap')
    ?.dataset.total?.trim();
  const hasRenderedResults = Boolean(renderedSearchQuery || fallbackResultCount);
  if (hasRenderedError) return null;
  if (
    !hasRenderedNoResults &&
    !hasRenderedResults &&
    (explicitState === 'empty' || explicitState === 'error')
  )
    return null;

  const searchTerm =
    renderedSearchQuery ||
    root.querySelector<HTMLInputElement>('.search-results__search-input')?.value.trim() ||
    new URL(location.href).searchParams.get('q')?.trim() ||
    root.dataset.searchTerm?.trim() ||
    '';
  if (!searchTerm) return null;

  const explicitResultCount = root.dataset.searchResults?.trim();
  const parsedExplicitResultCount = Number(explicitResultCount);
  const preferredResultCount =
    fallbackResultCount ??
    (explicitResultCount !== undefined &&
    explicitResultCount !== '' &&
    Number.isFinite(parsedExplicitResultCount)
      ? explicitResultCount
      : undefined);
  const parsedResultCount = Number(preferredResultCount ?? '0');
  const resultCount =
    Number.isFinite(parsedResultCount) && parsedResultCount > 0 ? parsedResultCount : 0;

  const isNoResults =
    hasRenderedNoResults || (!hasRenderedResults && explicitState === 'no-results');
  const searchResults = isNoResults ? 0 : resultCount;

  return {
    event: 'search_event',
    results_returned: searchResults > 0,
    search_results: searchResults,
    search_term: searchTerm
  };
}

export function trackSearchEvent(
  root: HTMLElement,
  location: Location = window.location,
  targetWindow: DataLayerWindow = window
): boolean {
  const event = extractSearchEvent(root, location);
  if (!event) return false;
  pushDataLayerEvent(event, targetWindow);
  return true;
}
